import type { LayoutBlock } from '@/storefront-runtime/types'
import {
  DEMO_PREMIUM_TECH_BUNDLE_IDS,
  DEMO_PREMIUM_TECH_SPOTLIGHT_ID,
  premiumTechCategorySlug,
} from '@/data/previewDemoVerticals'

/**
 * Futurer-style premium home layout (block order + defaults).
 * Only wired to the `premium-tech` store template (`storeTemplates.ts`, `getStoreTemplate('premium-tech')`).
 * Bundle grid + spotlight use the `premiumTech` preview catalog product ids (`pt-prod-*`); after applying the template, replace them with your catalog product UUIDs.
 * Hero / category / scroll / testimonial art lives under `public/premium-template/pro-temp/` (synced from your `Downloads/pict/pro temp` asset folders: iphone case, samsung case, airpods, apple watch case, air tags accessoirs).
 * There is no `footer` block type — the last row uses `rich-text` for a simple legal/footer band.
 */
export const futurerPremiumHomeBlocks: LayoutBlock[] = [
  {
    id: 'fut-ann-1',
    type: 'announcement-bar',
    props: {
      text: 'Livraison gratuite sur commandes éligibles · Retours simplifiés',
      link: '/shop',
      backgroundColor: '',
      textColor: '',
    },
  },
  {
    id: 'fut-marq-1',
    type: 'marquee-text',
    props: {
      lines: ['-50 %', 'Black Friday', '-50 %', 'Black Friday', 'FastSell'],
      direction: 'left',
      speed: 'medium',
      opacity: 12,
      ariaLabel: 'Texte défilant de marque',
    },
  },
  {
    id: 'fut-hero-1',
    type: 'hero-carousel',
    props: {
      sectionType: 'hero-carousel',
      autoplay: true,
      autoPlay: true,
      interval: 5200,
      showArrows: true,
      showDots: true,
      overlayOpacity: 58,
      height: 'lg',
      textAlign: 'left',
      pauseOnHover: true,
      secondaryCtaText: 'Voir tout',
      secondaryCtaLink: '/shop',
      emptyCarouselMessage: '',
      slides: [
        {
          id: 'fut-hc-1',
          title: 'Changez de style',
          subtitle: 'Coques audacieuses pour pliables — visuels pleine largeur pour l’aperçu premium-tech.',
          image: '/premium-template/category-1.jpg',
          backgroundImage: '',
          buttonText: 'Voir les coques',
          buttonLink: '/shop',
        },
        {
          id: 'fut-hc-2',
          title: 'Pensé pour se démarquer',
          subtitle: 'Coques AirPods et bracelets — vraies photos produit de votre bibliothèque.',
          image: '/premium-template/hero-carousel-flip-blue.png',
          backgroundImage: '',
          buttonText: 'Acheter maintenant',
          buttonLink: '/shop',
        },
        {
          id: 'fut-hc-3',
          title: 'Le son partout',
          subtitle: 'Des moments lifestyle et retail qui racontent toute l’histoire accessoires.',
          image: '',
          backgroundImage: '/premium-template/hero-carousel-airpods-orange.png',
          buttonText: 'Voir l’audio',
          buttonLink: '/shop',
        },
      ],
    },
  },
  {
    id: 'fut-spot-1',
    type: 'product-spotlight',
    props: {
      productId: DEMO_PREMIUM_TECH_SPOTLIGHT_ID,
      layout: 'image-right',
      description: '',
      specs: [
        { label: 'Profondeur', value: '0,5 in' },
        { label: 'Poids', value: '0,008 kg' },
        { label: 'Norme', value: 'IP68' },
      ],
      primaryCtaLabel: 'Ajouter au panier',
      glowEnabled: true,
      glowColor: '#7dd3fc',
      decorativeLines: ['Premium', 'Protection', 'Quotidien'],
    },
  },
  {
    id: 'fut-cat-1',
    type: 'category-showcase',
    props: {
      title: 'Parcourir par catégorie',
      subtitle: 'Chaque tuile pointe vers une vraie catégorie démo — coques séparées des laptops et de l’audio.',
      variant: 'hero-panels',
      columnsDesktop: 2,
      // Category slugs go through `premiumTechCategorySlug` so they always match `demoCategoriesPremiumTech`.
      tiles: [
        {
          slug: premiumTechCategorySlug('phone-cases'),
          title: 'Coques & accessoires',
          subtitle: 'Coques & protections — iPhone, Galaxy, pliables',
          image: '/premium-template/pro-temp/iphone-case.jpg',
          badge: '',
          ctaLabel: 'Voir tout',
          ctaHref: '',
          subItems: [
            { slug: premiumTechCategorySlug('phone-cases'), title: 'iPhone', image: '/premium-template/pro-temp/iphone-case.jpg' },
            { slug: premiumTechCategorySlug('phone-cases'), title: 'Samsung', image: '/premium-template/pro-temp/samsung-case.jpg' },
            { slug: premiumTechCategorySlug('phone-cases'), title: 'Pliables', image: '/premium-template/pro-temp/scroll-3.png' },
          ],
        },
        {
          slug: premiumTechCategorySlug('smart-home'),
          title: 'Maison connectée',
          subtitle: 'Caméras & maison connectée — l’audio est à part (lien dédié ci‑dessous)',
          image: '/premium-template/pro-temp/apple-watch.png',
          badge: '',
          ctaLabel: 'Voir tout',
          ctaHref: '',
          subItems: [
            {
              slug: premiumTechCategorySlug('smart-home'),
              title: 'Maison connectée',
              image: '/templates/local-webp/elx-cat-smart.webp',
            },
            {
              slug: premiumTechCategorySlug('audio'),
              title: 'Audio',
              image: '/premium-template/pro-temp/hero-airpods-watch.png',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'fut-pc-1',
    type: 'product-carousel',
    props: {
      title: 'Tendances du moment',
      subtitle: '',
      limit: 10,
      autoplay: false,
      showArrows: true,
      variant: 'split',
      sectionWidth: 'boxed',
      ctaLabel: 'Voir tout',
      ctaLink: '/shop',
      showQuickAdd: false,
      showPrice: true,
      showRating: false,
      columnsMobile: 2,
      columnsDesktop: 3,
    },
  },
  {
    id: 'fut-qv-1',
    type: 'quick-view',
    visible: true,
    props: { enabled: true, layout: 'advanced' },
  },
  {
    id: 'fut-scroll-1',
    type: 'scroll-showcase',
    props: {
      title: 'Pourquoi nous choisir',
      stickySide: 'left',
      steps: [
        {
          id: 'fut-ss-1',
          headline: 'Matériaux premium',
          body: 'Finitions métalliques et reliefs tactiles — cette colonne reste fixe pendant que vos visuels défilent.',
          image: '/premium-template/pro-temp/scroll-1.png',
          ctaLabel: 'Voir la collection',
          ctaHref: '/shop',
        },
        {
          id: 'fut-ss-2',
          headline: 'Une prise en main juste',
          body: 'Textures et motifs pensés pour le quotidien — mettez en avant chaque finition au fil du scroll.',
          image: '/premium-template/pro-temp/scroll-2.png',
          ctaLabel: '',
          ctaHref: '',
        },
        {
          id: 'fut-ss-3',
          headline: 'Conçu pour la vraie vie',
          body: 'Protection multicouche sans volume — associez photo forte et arguments à chaque étape.',
          image: '/premium-template/pro-temp/scroll-3.png',
          ctaLabel: '',
          ctaHref: '',
        },
        {
          id: 'fut-ss-4',
          headline: 'Un support quand il le faut',
          body: 'Des questions avant le paiement ? Notre équipe répond vite — moins de paniers abandonnés.',
          image: '/premium-template/pro-temp/scroll-4.png',
          ctaLabel: 'Contact',
          ctaHref: '/contact',
        },
      ],
    },
  },
  {
    id: 'fut-bundle-1',
    type: 'bundle-product-grid',
    props: {
      title: 'Pack produits',
      subtitle: 'Composez un set à partir de votre catalogue — texte merchandising ; branchez un vrai tarif pack au checkout si besoin.',
      productIds: [...DEMO_PREMIUM_TECH_BUNDLE_IDS, 'pt-prod-4'],
      panelTitle: 'Votre pack',
      thresholdMin: 3,
      discountPercent: 10,
      incentiveLine: 'Ajoutez {n}+ articles pour économiser {pct}%',
      unlockedLine: 'Réduction pack ({pct}%) appliquée au paiement.',
      emptyFootnote: 'Offre limitée : achetez maintenant pour économiser davantage.',
      primaryCtaLabel: 'Ajouter le pack au panier',
      showIndividualPrices: true,
      columnsDesktop: '3',
    },
  },
  {
    id: 'fut-tsc-1',
    type: 'testimonials-scattered',
    props: {
      title: 'Ce que disent nos clients',
      subtitle: '',
      initialIndex: 0,
      autoRotate: false,
      autoRotateIntervalMs: 6500,
      animationMode: 'float',
      items: [
        {
          id: 'fut-ts-1',
          quote: 'On sent vraiment la qualité — livraison rapide.',
          author: 'Michael B.',
          avatar: '/premium-template/pro-temp/testimonial-1.png',
          xPct: 12,
          yPct: 28,
          size: 'md',
        },
        {
          id: 'fut-ts-2',
          quote: 'Bonne protection sans être encombrant. Je recommande vivement !',
          author: 'Emma L.',
          avatar: '/premium-template/pro-temp/testimonial-2.png',
          xPct: 72,
          yPct: 22,
          size: 'lg',
        },
        {
          id: 'fut-ts-3',
          quote: 'Paiement fluide et support sympathique.',
          author: 'Samir T.',
          avatar: '/premium-template/pro-temp/testimonial-3.png',
          xPct: 46,
          yPct: 62,
          size: 'sm',
        },
      ],
    },
  },
  {
    id: 'fut-nl-1',
    type: 'newsletter',
    props: {
      title: 'Rejoindre la liste',
      subtitle: 'Nouveautés et offres exclusives.',
      placeholder: 'vous@exemple.com',
      ctaText: "S'inscrire",
      backgroundColor: '',
      backgroundImage: '',
      overlayOpacity: 50,
    },
  },
  {
    id: 'fut-foot-1',
    type: 'rich-text',
    props: {
      title: '',
      text: '© Nom de votre boutique. Tous droits réservés.\nLivraison · Retours · Confidentialité',
    },
  },
]

/**
 * Shop PLP for `premium-tech` only — not shared with base `electronics` block ids / copy.
 */
export const futurerPremiumShopBlocks: LayoutBlock[] = [
  {
    id: 'pt-shop-announce',
    type: 'announcement-bar',
    visible: true,
    props: {
      text: 'Accessoires premium · Expédition rapide · Retours sans tracas',
      link: '/shop',
      backgroundColor: '#0c4a6e',
      textColor: '#f0f9ff',
    },
  },
  {
    id: 'pt-shop-cats',
    type: 'category-strip',
    visible: true,
    props: {},
  },
  {
    id: 'pt-shop-featured',
    type: 'featured-collection',
    visible: true,
    props: {
      title: 'Sélection de l’équipe',
      subtitle: 'Coques, audio et essentials — best-sellers démo',
      limit: 4,
      columns: 4,
      viewAllLabel: '',
      viewAllLink: '',
    },
  },
  {
    id: 'pt-shop-grid',
    type: 'product-grid',
    visible: true,
    props: {
      title: 'Catalogue complet',
      subtitle: 'Coques, laptops, audio & smart home — chaque filtre suit le catalogue démo premium-tech',
      limit: 24,
      columns: 4,
      anchorId: 'catalog',
    },
  },
]

/**
 * PDP stack for `premium-tech` only — distinct block ids from `electronics` template.
 */
export const futurerPremiumProductBlocks: LayoutBlock[] = [
  {
    id: 'pt-pdp-details',
    type: 'product-details',
    visible: true,
    props: {
      highlights: [
        'Pensé pour le quotidien — matériaux premium et ajustement précis',
        '220 V / 50 Hz lorsque applicable — voir la fiche produit',
        'Des questions ? Écrivez-nous sur WhatsApp avant d’acheter',
      ],
      trustShipping: 'Envoi suivi avec emballage protecteur. Délais affichés au paiement.',
      trustReturns: 'Rétractation ? Retournez les articles non utilisés sous 7 jours dans leur état d’origine.',
      trustPayment: 'Paiement à la livraison si disponible, plus D17, Flouci, Konnect — même checkout sécurisé FastSell.',
    },
  },
  {
    id: 'pt-pdp-tabs',
    type: 'product-tabs',
    visible: true,
    props: {
      tabs: [
        {
          label: 'Détails',
          content:
            'Fiche produit démo Premium Tech — caractéristiques et contenu suivent votre catalogue réel une fois connecté.',
        },
        {
          label: 'Compatibilité',
          content:
            'Utilisez cet onglet pour les notes de compatibilité (modèles de coques, tailles de montre, génération AirPods). Remplacez par votre texte dans l’éditeur.',
        },
        {
          label: 'Garantie',
          content: 'Conditions de garantie locales selon le vendeur. Conservez la preuve d’achat pour un SAV plus rapide.',
        },
      ],
    },
  },
  {
    id: 'pt-pdp-related',
    type: 'related-products',
    visible: true,
    props: {
      title: 'S’associe bien avec',
      limit: 4,
      columns: 4,
    },
  },
]

/**
 * Category PLP for `premium-tech` only — keeps `/preview-template/premium-tech/.../category/:slug`
 * visually and structurally distinct from the default `electronics` template category page.
 */
export const futurerPremiumCategoryBlocks: LayoutBlock[] = [
  {
    id: 'pt-cat-announce',
    type: 'announcement-bar',
    visible: true,
    props: {
      text: 'Coques · bracelets · écouteurs — nouveautés chaque semaine · Retours faciles',
      link: '/shop',
      backgroundColor: '#0c4a6e',
      textColor: '#f0f9ff',
    },
  },
  {
    id: 'pt-cat-hero',
    type: 'promo-banner',
    visible: true,
    props: {
      text: 'Aperçu Futurer : chaque catégorie affiche uniquement le catalogue démo de ce modèle — les filtres restent alignés sur votre niche.',
      highlight: 'Premium Tech',
      backgroundColor: '#e0f2fe',
      textColor: '#0c4a6e',
      ctaLabel: 'Voir tout',
      ctaLink: '/shop',
      showIcon: true,
    },
  },
  {
    id: 'pt-cat-strip',
    type: 'category-strip',
    visible: true,
    props: {},
  },
  {
    id: 'pt-cat-grid',
    type: 'category-grid',
    visible: true,
    props: {},
  },
]

/** Alias matching the requested export name. */
export const futurerTemplate = futurerPremiumHomeBlocks
